import type { StudentIdentitySummary } from '@/components/students/StudentIdentity';
import { API_BASE_URL, fetchApi, fetchApiBlob } from '@/services/api-client';

const PATH = '/tuition';

export type TuitionComponent =
  | 'tuition'
  | 'campus_development'
  | 'technology'
  | 'lunch'
  | 'graduation'
  | 'english_language_development'
  | 'learning_support'
  | 'one_to_one_support';

export const TUITION_COMPONENTS: TuitionComponent[] = [
  'tuition',
  'campus_development',
  'technology',
  'lunch',
  'graduation',
  'english_language_development',
  'learning_support',
  'one_to_one_support',
];

/** What the fee table may hold: everything charged by grade. Per-student services go on a draft instead. */
export const FEE_TABLE_COMPONENTS: TuitionComponent[] = [
  'tuition',
  'campus_development',
  'technology',
  'lunch',
  'graduation',
];

/** Fees added per student as a line on their draft, never by grade. Mirrors the API's AS_NEEDED_DEFAULT_AMOUNT. */
export const AS_NEEDED: { component: TuitionComponent; label: string; amount: number }[] = [
  { component: 'one_to_one_support', label: '1:1 Support', amount: 30000 },
  { component: 'english_language_development', label: 'English Language Development', amount: 30000 },
  { component: 'learning_support', label: 'Learning Support', amount: 40000 },
];

export type Semester = 1 | 2;

export interface FeeType {
  key: TuitionComponent;
  /** As printed on the invoice. */
  label: string;
  charged: 'by_grade' | 'per_student';
  discountApplies: boolean;
  defaultAmount: number | null;
}

export interface FeeRate {
  component: TuitionComponent;
  gradeFrom: number;
  gradeTo: number;
  /** Null: both semesters. */
  semester: Semester | null;
  amount: number;
}

export interface TuitionStudent {
  studentId: string;
  studentName: string;
  student: StudentIdentitySummary;
  gradeLevel: number;
  gradeLabel: string;
  discount: { percent: number; reason: string; grantedByName: string | null; grantedAt: string } | null;
  note: string | null;
  prepaid: TuitionPrepaid | null;
}

export interface TuitionPrepaid {
  /** First calendar year of the first school year paid for. */
  fromStartYear: number;
  fromSemester: Semester;
  /** First calendar year of the last school year paid for, e.g. 2027 for 2027–28. */
  untilStartYear: number;
  untilSemester: Semester;
  note: string;
}

export interface TuitionRoster {
  kind: 'enrolled' | 'projected' | 'empty';
  sourceYearName: string | null;
}

export interface TuitionStudentsResult {
  roster: TuitionRoster;
  students: TuitionStudent[];
  copyableDiscounts: number;
  copyableFromName: string | null;
}

export interface CopyDiscountsResult {
  copied: number;
  left: { studentId: string; studentName: string }[];
}

export type LineKind = TuitionComponent | 'custom';

export interface ChargeLine {
  component: LineKind;
  label: string;
  gross: number;
  adjustment: number;
  adjustmentLabel: string | null;
  net: number;
}

export type ChargeStatus = 'draft' | 'issued' | 'settled' | 'cancelled';

/** All invoices, or only the semester fees, or only the extras. */
export type TuitionChargeKindFilter = 'all' | TuitionCharge['kind'];

export interface TuitionCharge {
  id: string;
  studentId: string;
  studentName: string;
  /** For the list's identity cell; null where the API did not load the student. */
  student: StudentIdentitySummary | null;
  gradeLabel: string;
  status: ChargeStatus;
  kind: 'semester' | 'extra';
  gross: number;
  adjustmentTotal: number;
  net: number;
  /** What the family paid in advance toward this invoice; null when it is not prepaid. */
  prepaidAmount: number | null;
  lines: ChargeLine[];
  /** What an extra invoice is for; null on a semester invoice. */
  title: string | null;
  note: string | null;
  edited: boolean;
  /** Confirmed against the bank. */
  paid: number;
  /** Reported by the family and not yet checked by the office. */
  reported: number;
  invoiceNumber: string | null;
  dueDate: string | null;
  hasInvoicePdf: boolean;
  issuedAt: string | null;
  emailedAt: string | null;
  remindedAt: string | null;
}

export interface GenerateResult {
  created: number;
  kept: number;
  skipped: { studentId: string; studentName: string; reason: string }[];
  /** Hand-edited drafts kept as they are that no longer match the fee table, discount or enrolment. */
  stale: { studentId: string; studentName: string; reason: string }[];
}

export interface InvoiceJob {
  state: 'running' | 'done' | 'failed';
  done: number;
  total: number;
  failed: { chargeId: string; studentName: string; error: string }[];
}

export type EditableLine = Omit<ChargeLine, 'net'>;

export type NoticeKind = 'invoices' | 'reminders';
export interface NoticePreview {
  invoicesToEmail: number;
  parentsToEmail: number;
  withoutRecipients: number;
  unpaid: number;
  unpaidParents: number;
}
export interface NoticeJob {
  kind: NoticeKind;
  state: 'running' | 'done' | 'failed';
  done: number;
  total: number;
  emails: number;
  failed: number;
  withoutRecipients: number;
}

export type ReceiptCopyStatus = 'requested' | 'ready' | 'collected';
export interface ReceiptCopyRequest {
  id: string;
  receiptId: string;
  receiptNumber: string;
  studentName: string;
  amount: number;
  requestedByName: string | null;
  requestedAt: string;
  status: ReceiptCopyStatus;
  readyAt: string | null;
  collectedAt: string | null;
}

export type TuitionPaymentMethod = 'bank_transfer' | 'cheque' | 'cash' | 'credit_card' | 'salary_deduction';
export const TUITION_PAYMENT_METHODS: TuitionPaymentMethod[] = [
  'bank_transfer',
  'cheque',
  'cash',
  'credit_card',
  'salary_deduction',
];
export type TuitionPaymentStatus = 'declared' | 'confirmed' | 'rejected';
export type TuitionFamilyStatus = 'to_check' | 'owing' | 'paid';

/** One invoice's part of a transfer: what settles it, and the receipt issued for it. */
export interface TuitionTransferShare {
  paymentId: string;
  chargeId: string;
  amount: number;
  studentName: string;
  invoiceNumber: string | null;
  semester: Semester;
  schoolYearName: string;
  receiptId: string | null;
  receiptNumber: string | null;
  receiptHasPdf: boolean;
}

/**
 * One movement of money from a family — a reported bank transfer with its slip, or money the
 * office took at the desk — shared out over the invoices it pays and reviewed as a whole.
 */
export interface TuitionTransfer {
  id: string;
  amount: number;
  method: TuitionPaymentMethod;
  status: TuitionPaymentStatus;
  note: string | null;
  declaredAt: string;
  declaredByName: string | null;
  receivedOn: string | null;
  reviewedAt: string | null;
  /** Why the office could not match it. Written to be read by the family. */
  reviewNote: string | null;
  hasDocument: boolean;
  documentName: string | null;
  shares: TuitionTransferShare[];
}

export interface TuitionFamilyInvoice {
  chargeId: string;
  studentId: string;
  invoiceNumber: string | null;
  kind: 'semester' | 'extra';
  title: string | null;
  net: number;
  dueDate: string | null;
  status: ChargeStatus;
  /** Confirmed against the bank. */
  confirmed: number;
  /** Reported by the family and not yet checked. */
  waiting: number;
  hasPdf: boolean;
}

/** Children who share a parent, directly or through a sibling, with their invoices for one semester. */
export interface TuitionFamily {
  key: string;
  parentNames: string[];
  children: StudentIdentitySummary[];
  invoices: TuitionFamilyInvoice[];
  invoiced: number;
  confirmed: number;
  waiting: number;
  stillToCome: number;
  status: TuitionFamilyStatus;
  lastReportedAt: string | null;
  /** `otherShares` counts the shares of a transfer that pay invoices outside this semester. */
  transfers: (TuitionTransfer & { otherShares: number })[];
}

export interface TuitionFamiliesResult {
  totals: {
    invoiced: number;
    confirmed: number;
    waiting: number;
    stillToCome: number;
    families: { total: number; toCheck: number; owing: number; paid: number };
  };
  families: TuitionFamily[];
}

export interface TransferShareInput {
  chargeId: string;
  amount: number;
}

const json = (method: string, body?: unknown) => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});
const year = (yearId: string) => `${PATH}/years/${yearId}`;
const sem = (yearId: string, semester: Semester) => `${year(yearId)}/semesters/${semester}`;
const kindQuery = (kind: TuitionChargeKindFilter) => (kind === 'all' ? '' : `?kind=${kind}`);

export const tuitionApi = {
  feeTypes: () => fetchApi<FeeType[]>(`${PATH}/fee-types`),
  fees: (yearId: string) => fetchApi<FeeRate[]>(`${year(yearId)}/fees`),
  replaceFees: (yearId: string, rows: FeeRate[]) => fetchApi<FeeRate[]>(`${year(yearId)}/fees`, json('PUT', { rows })),
  fillPublished: (yearId: string) => fetchApi<FeeRate[]>(`${year(yearId)}/fees/published`, json('POST')),

  students: (yearId: string) => fetchApi<TuitionStudentsResult>(`${year(yearId)}/students`),
  grantDiscount: (yearId: string, studentId: string, input: { percent: number; reason: string }) =>
    fetchApi<void>(`${year(yearId)}/students/${studentId}/discount`, json('POST', input)),
  withdrawDiscount: (yearId: string, studentId: string) =>
    fetchApi<void>(`${year(yearId)}/students/${studentId}/discount`, json('DELETE')),
  setNote: (yearId: string, studentId: string, note: string | null) =>
    fetchApi<void>(`${year(yearId)}/students/${studentId}/note`, json('PUT', { note })),

  copyDiscounts: (yearId: string) => fetchApi<CopyDiscountsResult>(`${year(yearId)}/discounts/copy`, json('POST')),

  setPrepaid: (studentId: string, input: TuitionPrepaid) =>
    fetchApi<void>(`${PATH}/students/${studentId}/prepaid`, json('PUT', input)),
  clearPrepaid: (studentId: string) => fetchApi<void>(`${PATH}/students/${studentId}/prepaid`, json('DELETE')),

  charges: (yearId: string, semester: Semester) => fetchApi<TuitionCharge[]>(`${sem(yearId, semester)}/charges`),
  generate: (yearId: string, semester: Semester) =>
    fetchApi<GenerateResult>(`${sem(yearId, semester)}/generate`, json('POST')),
  generateOne: (yearId: string, semester: Semester, studentId: string) =>
    fetchApi<TuitionCharge>(`${sem(yearId, semester)}/students/${studentId}/draft`, json('POST')),
  createExtra: (
    yearId: string,
    semester: Semester,
    studentId: string,
    input: { title: string; lines: EditableLine[]; note: string | null }
  ) => fetchApi<TuitionCharge>(`${sem(yearId, semester)}/students/${studentId}/extra`, json('POST', input)),
  editCharge: (id: string, input: { lines: EditableLine[]; note: string | null; title?: string }) =>
    fetchApi<TuitionCharge>(`${PATH}/charges/${id}`, json('PUT', input)),
  resetCharge: (id: string) => fetchApi<TuitionCharge>(`${PATH}/charges/${id}/reset`, json('POST')),
  issueCharge: (id: string, dueDate: string) =>
    fetchApi<TuitionCharge>(`${PATH}/charges/${id}/issue`, json('POST', { dueDate })),
  issueAll: (yearId: string, semester: Semester, dueDate: string) =>
    fetchApi<{ issued: number }>(`${sem(yearId, semester)}/issue`, json('POST', { dueDate })),
  cancelCharge: (id: string, reason: string) =>
    fetchApi<void>(`${PATH}/charges/${id}/cancel`, json('POST', { reason })),
  startInvoices: (yearId: string, semester: Semester, onlyMissing: boolean) =>
    fetchApi<InvoiceJob>(`${sem(yearId, semester)}/invoices`, json('POST', { onlyMissing })),
  invoiceProgress: (yearId: string, semester: Semester) =>
    fetchApi<InvoiceJob | null>(`${sem(yearId, semester)}/invoices/progress`),
  invoicePdf: (id: string) => fetchApiBlob(`${PATH}/charges/${id}/invoice.pdf`),
  invoicePreview: (id: string) => fetchApiBlob(`${PATH}/charges/${id}/preview.pdf`),
  chargesCsv: (yearId: string, semester: Semester) => fetchApiBlob(`${sem(yearId, semester)}/charges.csv`),

  families: (yearId: string, semester: Semester, kind: TuitionChargeKindFilter = 'all') =>
    fetchApi<TuitionFamiliesResult>(`${sem(yearId, semester)}/families${kindQuery(kind)}`),
  familiesCsv: (yearId: string, semester: Semester, kind: TuitionChargeKindFilter = 'all') =>
    fetchApiBlob(`${sem(yearId, semester)}/families.csv${kindQuery(kind)}`),
  /** Money the office took itself, over one or several invoices; confirmed as it is recorded. */
  recordTransfer: (input: {
    shares: TransferShareInput[];
    method: TuitionPaymentMethod;
    receivedOn: string;
    note?: string;
  }) => fetchApi<TuitionTransfer>(`${PATH}/transfers`, json('POST', input)),
  /** `expectedAmount` is what the office was looking at: the API refuses if the family has changed the report since. */
  confirmTransfer: (
    id: string,
    input: {
      amount: number;
      expectedAmount: number;
      shares?: TransferShareInput[];
      method?: TuitionPaymentMethod;
      receivedOn?: string;
    }
  ) => fetchApi<TuitionTransfer>(`${PATH}/transfers/${id}/confirm`, json('POST', input)),
  rejectTransfer: (id: string, input: { note: string; expectedAmount: number }) =>
    fetchApi<TuitionTransfer>(`${PATH}/transfers/${id}/reject`, json('POST', input)),
  /** Opened in a new tab, so absolute: a bare path would resolve against the admin's own origin. */
  transferSlipUrl: (id: string) => `${API_BASE_URL}${PATH}/transfers/${id}/slip`,
  createReceipt: (paymentId: string) =>
    fetchApi<{ number: string }>(`${PATH}/payments/${paymentId}/receipt`, json('POST')),
  receiptPdf: (receiptId: string) => fetchApiBlob(`${PATH}/receipts/${receiptId}/pdf`),

  noticePreview: (yearId: string, semester: Semester) =>
    fetchApi<NoticePreview>(`${sem(yearId, semester)}/notices/preview`),
  startNotices: (yearId: string, semester: Semester, kind: NoticeKind) =>
    fetchApi<NoticeJob>(`${sem(yearId, semester)}/notices/${kind}`, json('POST')),
  noticeProgress: (yearId: string, semester: Semester, kind: NoticeKind) =>
    fetchApi<NoticeJob | null>(`${sem(yearId, semester)}/notices/progress?kind=${kind}`),

  receiptCopies: () => fetchApi<ReceiptCopyRequest[]>(`${PATH}/receipt-copies`),
  copyReady: (id: string) => fetchApi<void>(`${PATH}/receipt-copies/${id}/ready`, json('POST')),
  copyCollected: (id: string) => fetchApi<void>(`${PATH}/receipt-copies/${id}/collected`, json('POST')),
  voidReceipt: (receiptId: string, reason: string) =>
    fetchApi<{ number: string }>(`${PATH}/receipts/${receiptId}/void`, json('POST', { reason })),
};
